import type { PremiumFeedbackItem } from "../api/resources";

/**
 * The free tier's coaching voice, made in the browser (owner, 2026-10-13: "Kokoro only"). The server writes the
 * sentences (freeCoaching); Kokoro speaks them here, in a worker, one at a time in order, and the live view gets each
 * clip as soon as it is ready. The model (~90 MB) is fetched once and cached by the browser.
 */
export interface SpokenClip {
  url: string;
  seconds: number;
}
export type Speak = (text: string) => Promise<SpokenClip | null>;

/** A British male voice, the nearest Kokoro has to the premium narrator. */
export const KOKORO_VOICE = "bm_george";

/** Voices the items one at a time, in order; after each, reports the items so far (voiced ones carry their clip and
 * its real length). A sentence that cannot be spoken stays text only. Never throws. */
export async function voiceInOrder(
  items: PremiumFeedbackItem[],
  speak: Speak,
  onUpdate: (items: PremiumFeedbackItem[]) => void,
  cancelled: () => boolean
): Promise<void> {
  let current = items.map((it) => ({ ...it }));
  for (let i = 0; i < current.length; i++) {
    if (cancelled()) return;
    let clip: SpokenClip | null = null;
    try {
      clip = await speak(current[i].text);
    } catch {
      clip = null;
    }
    if (cancelled()) return;
    if (clip) {
      current = current.map((it, n) => (n === i ? { ...it, audio: clip!.url, seconds: clip!.seconds } : it));
    }
    onUpdate(current);
  }
}

interface WorkerReply {
  id: number;
  wav?: Blob;
  seconds?: number;
  error?: string;
}

/** Speaks through the Kokoro worker, started on the first sentence. No worker, or a worker error: no clip. */
export function kokoroSpeaker(makeWorker: () => Worker): Speak {
  let worker: Worker | null = null;
  let nextId = 1;
  const waiting = new Map<number, (reply: WorkerReply) => void>();
  return (text: string) =>
    new Promise<SpokenClip | null>((resolve) => {
      try {
        if (!worker) {
          worker = makeWorker();
          worker.onmessage = (e: MessageEvent<WorkerReply>) => {
            const done = waiting.get(e.data.id);
            waiting.delete(e.data.id);
            done?.(e.data);
          };
        }
        const id = nextId++;
        waiting.set(id, (reply) => {
          if (reply.error || !reply.wav) resolve(null);
          else resolve({ url: URL.createObjectURL(reply.wav), seconds: reply.seconds ?? 0 });
        });
        worker.postMessage({ id, text, voice: KOKORO_VOICE });
      } catch {
        resolve(null);
      }
    });
}

// One Kokoro worker for the page, so the model fetched while the clip is processing is the one that voices it.
let shared: Speak | null = null;
let warmed = false;

/** The page's one Kokoro speaker (its worker started on first use). */
export function sharedKokoroSpeaker(makeWorker: () => Worker): Speak {
  if (!shared) shared = kokoroSpeaker(makeWorker);
  return shared;
}

/** Start loading the voice model now, while the upload is still processing (owner, 2026-10-18 onboarding plan): the
 * ~90 MB download used to begin only once the result had landed, so the first stop waited for it. Once per page. */
export function warmKokoro(makeWorker: () => Worker): void {
  if (warmed) return;
  warmed = true;
  void sharedKokoroSpeaker(makeWorker)("Ready.").then((clip) => {
    if (clip) {
      try {
        URL.revokeObjectURL?.(clip.url);
      } catch {
        /* nothing to free */
      }
    }
  });
}

export function resetSharedKokoroForTests(): void {
  shared = null;
  warmed = false;
}
